/**
 * Treppan Academy - Leaderboard & Gamification
 * User rankings, badges, streaks, and achievements
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Medal, Star, Flame, Target, Award, Crown,
  ChevronRight, GraduationCap, BookOpen, Clock, Zap,
  TrendingUp, Users
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  gold: '#D4AF37',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  success: '#10B981',
  purple: '#A855F7',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

// Badge definitions
const BADGES = [
  { id: 'first_course', name: 'First Steps', description: 'Complete your first course', icon: BookOpen, color: colors.success },
  { id: 'fast_learner', name: 'Fast Learner', description: 'Complete a course in under 1 hour', icon: Zap, color: colors.gold },
  { id: 'perfectionist', name: 'Perfectionist', description: 'Score 100% on an exam', icon: Target, color: colors.purple },
  { id: 'distinction', name: 'Distinction', description: 'Earn a Distinction grade', icon: Star, color: colors.gold },
  { id: 'triple_threat', name: 'Triple Threat', description: 'Complete 3 courses', icon: Trophy, color: colors.bronze },
  { id: 'streak_7', name: 'Week Warrior', description: '7-day learning streak', icon: Flame, color: '#EF4444' },
  { id: 'all_courses', name: 'Academy Master', description: 'Complete all available courses', icon: Crown, color: colors.gold },
  { id: 'top_10', name: 'Elite Learner', description: 'Reach top 10 on the leaderboard', icon: Medal, color: colors.silver },
];

const LeaderboardRow = ({ rank, user, isCurrentUser }) => {
  const getRankStyle = (rank) => {
    if (rank === 1) return { bg: `${colors.gold}20`, color: colors.gold, icon: Crown };
    if (rank === 2) return { bg: `${colors.silver}20`, color: colors.silver, icon: Medal };
    if (rank === 3) return { bg: `${colors.bronze}20`, color: colors.bronze, icon: Medal };
    return { bg: colors.background, color: colors.textMuted, icon: null };
  };

  const style = getRankStyle(rank);
  const RankIcon = style.icon;

  return (
    <div 
      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${isCurrentUser ? 'ring-2' : ''}`}
      style={{ 
        backgroundColor: isCurrentUser ? `${colors.gold}10` : style.bg,
        border: `1px solid ${isCurrentUser ? colors.gold : colors.border}`,
        ringColor: colors.gold
      }}
    >
      {/* Rank */}
      <div 
        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${style.color}20` }}
      >
        {RankIcon ? (
          <RankIcon className="h-6 w-6" style={{ color: style.color }} />
        ) : (
          <span className="text-lg font-bold" style={{ color: style.color }}>{rank}</span>
        )}
      </div>

      {/* User Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p 
            className="font-semibold truncate"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            {user.name}
            {isCurrentUser && (
              <span className="ml-2 text-xs" style={{ color: colors.gold }}>(You)</span>
            )}
          </p>
          {user.streak > 0 && (
            <Badge 
              className="flex items-center gap-1"
              style={{ backgroundColor: '#EF444420', color: '#EF4444' }}
            >
              <Flame className="h-3 w-3" />
              {user.streak}
            </Badge>
          )}
        </div>
        <p className="text-sm" style={{ color: colors.textMuted }}>
          {user.courses_completed} courses • {user.certificates} certificates
        </p>
      </div>

      {/* Score */}
      <div className="text-right">
        <p 
          className="text-2xl font-bold"
          style={{ color: colors.gold, fontFamily: 'Outfit, sans-serif' }}
        >
          {user.points}
        </p>
        <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>
          Points
        </p>
      </div>
    </div>
  );
};

const BadgeCard = ({ badge, earned }) => {
  const Icon = badge.icon;
  
  return (
    <div 
      className={`p-4 rounded-xl text-center transition-all ${earned ? '' : 'opacity-40 grayscale'}`}
      style={{ 
        backgroundColor: colors.surface, 
        border: `1px solid ${earned ? badge.color : colors.border}30`
      }}
    >
      <div 
        className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
        style={{ backgroundColor: `${badge.color}20` }}
      >
        <Icon className="h-8 w-8" style={{ color: badge.color }} />
      </div>
      <h4 className="font-semibold text-sm mb-1" style={{ color: colors.textHeading }}>
        {badge.name}
      </h4>
      <p className="text-xs" style={{ color: colors.textMuted }}>
        {badge.description}
      </p>
      {earned && (
        <Badge 
          className="mt-2"
          style={{ backgroundColor: `${colors.success}20`, color: colors.success }}
        >
          Earned
        </Badge>
      )}
    </div>
  );
};

// Team Leaderboard Component
const TeamLeaderboard = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/teams/leaderboard?token=${token}`);
      const result = await res.json();
      if (result.success) {
        setTeamData(result.data);
      }
    } catch (error) {
      console.error('Error fetching team leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 mx-auto" style={{ borderColor: colors.gold }} />
      </div>
    );
  }

  const teams = teamData?.teams || [];
  const myTeam = teamData?.my_team;

  if (teams.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
        <p style={{ color: colors.textMuted }}>No teams found in your organization</p>
        <p className="text-sm mt-2" style={{ color: colors.textMuted }}>
          Team rankings will appear once teams are set up
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* My Team Highlight */}
      {myTeam && (
        <div 
          className="rounded-xl p-4 mb-6"
          style={{ 
            backgroundColor: `${colors.gold}10`,
            border: `2px solid ${colors.gold}`
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${colors.gold}20` }}
              >
                <Crown className="h-6 w-6" style={{ color: colors.gold }} />
              </div>
              <div>
                <p className="font-bold" style={{ color: colors.textHeading }}>
                  Your Team: {myTeam.name}
                </p>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  Rank #{myTeam.rank} • {myTeam.members_count} members
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: colors.gold }}>
                {myTeam.total_points}
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>Total Points</p>
            </div>
          </div>
        </div>
      )}

      {/* Team Rankings */}
      {teams.map((team, index) => {
        const getRankStyle = (rank) => {
          if (rank === 1) return { bg: `${colors.gold}20`, color: colors.gold, icon: Crown };
          if (rank === 2) return { bg: `${colors.silver}20`, color: colors.silver, icon: Medal };
          if (rank === 3) return { bg: `${colors.bronze}20`, color: colors.bronze, icon: Medal };
          return { bg: colors.background, color: colors.textMuted, icon: null };
        };
        
        const style = getRankStyle(team.rank);
        const RankIcon = style.icon;
        const isMyTeam = myTeam?.id === team.id;

        return (
          <div 
            key={team.id}
            className={`flex items-center gap-4 p-4 rounded-xl transition-all ${isMyTeam ? 'ring-2' : ''}`}
            style={{ 
              backgroundColor: isMyTeam ? `${colors.gold}10` : style.bg,
              border: `1px solid ${isMyTeam ? colors.gold : colors.border}`,
              ringColor: colors.gold
            }}
            data-testid={`team-row-${index}`}
          >
            {/* Rank */}
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${style.color}20` }}
            >
              {RankIcon ? (
                <RankIcon className="h-6 w-6" style={{ color: style.color }} />
              ) : (
                <span className="text-lg font-bold" style={{ color: style.color }}>{team.rank}</span>
              )}
            </div>

            {/* Team Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold truncate" style={{ color: colors.textHeading }}>
                  {team.name}
                </p>
                {isMyTeam && (
                  <Badge style={{ backgroundColor: `${colors.gold}20`, color: colors.gold }}>
                    Your Team
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-sm" style={{ color: colors.textMuted }}>
                  {team.members_count} members
                </span>
                <span className="text-sm" style={{ color: colors.textMuted }}>
                  {team.active_learners} active
                </span>
                <span className="text-sm" style={{ color: colors.success }}>
                  {team.courses_completed} completed
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: colors.textHeading }}>
                  {team.certificates}
                </p>
                <p className="text-xs" style={{ color: colors.textMuted }}>Certs</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: colors.gold }}>
                  {team.total_points}
                </p>
                <p className="text-xs" style={{ color: colors.textMuted }}>Points</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: colors.purple }}>
                  {team.completion_rate}%
                </p>
                <p className="text-xs" style={{ color: colors.textMuted }}>Rate</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const { token, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('leaderboard');

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/leaderboard?token=${token}`);
      const result = await res.json();
      
      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <Trophy className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  const leaderboard = data?.leaderboard || [];
  const userStats = data?.user_stats || {};
  const userBadges = data?.user_badges || [];
  const myRank = data?.my_rank || 0;

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl" style={{ backgroundColor: `${colors.gold}15` }}>
            <Trophy className="h-6 w-6" style={{ color: colors.gold }} />
          </div>
          <h1 
            className="text-3xl font-bold tracking-tight"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Leaderboard
          </h1>
        </div>
        <p style={{ color: colors.textBody }}>
          Compete with other learners and earn badges
        </p>
      </div>

      {/* My Stats Card */}
      <div 
        className="rounded-xl p-6 mb-8"
        style={{ 
          backgroundColor: colors.surface, 
          border: `1px solid ${colors.border}`,
          background: `linear-gradient(135deg, ${colors.gold}10 0%, transparent 50%)`
        }}
      >
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <div className="text-center">
            <p className="text-4xl font-bold" style={{ color: colors.gold, fontFamily: 'Outfit, sans-serif' }}>
              #{myRank || '-'}
            </p>
            <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>Your Rank</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold" style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}>
              {userStats.points || 0}
            </p>
            <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>Total Points</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold" style={{ color: colors.success, fontFamily: 'Outfit, sans-serif' }}>
              {userStats.courses_completed || 0}
            </p>
            <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>Courses</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold" style={{ color: colors.purple, fontFamily: 'Outfit, sans-serif' }}>
              {userStats.certificates || 0}
            </p>
            <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>Certificates</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Flame className="h-8 w-8" style={{ color: '#EF4444' }} />
              <p className="text-4xl font-bold" style={{ color: '#EF4444', fontFamily: 'Outfit, sans-serif' }}>
                {userStats.streak || 0}
              </p>
            </div>
            <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>Day Streak</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList 
          className="w-full justify-start gap-2 bg-transparent p-0"
          style={{ borderBottom: `1px solid ${colors.border}` }}
        >
          <TabsTrigger 
            value="leaderboard"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold"
            style={{ color: colors.textBody }}
            data-testid="tab-rankings"
          >
            <Users className="h-4 w-4 mr-2" />
            Individual Rankings
          </TabsTrigger>
          <TabsTrigger 
            value="teams"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold"
            style={{ color: colors.textBody }}
            data-testid="tab-teams"
          >
            <Users className="h-4 w-4 mr-2" />
            Team Rankings
          </TabsTrigger>
          <TabsTrigger 
            value="badges"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold"
            style={{ color: colors.textBody }}
            data-testid="tab-badges"
          >
            <Award className="h-4 w-4 mr-2" />
            Badges
          </TabsTrigger>
        </TabsList>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4">
          {leaderboard.length > 0 ? leaderboard.map((user, index) => (
            <LeaderboardRow 
              key={user.id || index}
              rank={index + 1}
              user={user}
              isCurrentUser={user.id === currentUser?.id}
            />
          )) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
              <p style={{ color: colors.textMuted }}>No learners yet</p>
            </div>
          )}
        </TabsContent>
        
        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-4">
          <TeamLeaderboard token={token} />
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {BADGES.map(badge => (
              <BadgeCard 
                key={badge.id}
                badge={badge}
                earned={userBadges.includes(badge.id)}
              />
            ))}
          </div>
          
          {/* Progress to next badge */}
          <div 
            className="mt-8 rounded-xl p-6"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
          >
            <h3 className="font-semibold mb-4" style={{ color: colors.textHeading }}>
              Progress to Next Badge
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span style={{ color: colors.textBody }}>Triple Threat (3 courses)</span>
                  <span style={{ color: colors.gold }}>
                    {userStats.courses_completed || 0}/3
                  </span>
                </div>
                <Progress 
                  value={((userStats.courses_completed || 0) / 3) * 100}
                  className="h-2"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span style={{ color: colors.textBody }}>Week Warrior (7-day streak)</span>
                  <span style={{ color: '#EF4444' }}>
                    {userStats.streak || 0}/7
                  </span>
                </div>
                <Progress 
                  value={((userStats.streak || 0) / 7) * 100}
                  className="h-2"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
